import type { Locale } from '@/i18n'
import { guaranteeSheetUrlFor } from './guaranteeSheets'

/**
 * Bilingual guarantee tables per formula (plan slug), the single source of truth
 * shared by the sales journey (JourneyStartCard) and the connected space
 * (contract mapper). Each entry holds a short summary + the guarantee rows,
 * keyed by locale.
 */
export type PlanDetail = { summary: string; rows: { label: string; value: string }[] }

/** Complémentaire santé : minimal / essential / confort / allRisk. */
export const planDetails: Partial<Record<string, Record<string, PlanDetail>>> = {
  minimal: {
    fr: {
      summary: "La solution santé économique, couvrant les frais d'hospitalisation.",
      rows: [
        { label: 'Médecin et pharmacie', value: '—' },
        { label: 'Optique (lunettes, lentilles)', value: '—' },
        { label: 'Dentaire (soins, couronnes)', value: '—' },
        { label: 'Hospitalisation', value: 'Couverture de base' },
        { label: 'Médecines douces', value: '—' },
      ],
    },
    en: {
      summary: 'The budget-friendly health solution, covering hospitalisation costs.',
      rows: [
        { label: 'Doctor & pharmacy', value: '—' },
        { label: 'Optical (glasses, lenses)', value: '—' },
        { label: 'Dental (care, crowns)', value: '—' },
        { label: 'Hospitalisation', value: 'Basic cover' },
        { label: 'Alternative medicine', value: '—' },
      ],
    },
  },
  essential: {
    fr: {
      summary: 'La couverture santé incluant les remboursements de santé de base + tes rdv chez tes spécialistes.',
      rows: [
        { label: 'Médecin et pharmacie', value: 'Couverture de base (hors Psy et honoraires paramédicaux : masseurs-kiné, pédicures, podologues, infirmières...)' },
        { label: 'Optique (lunettes, lentilles)', value: 'Couverture de base' },
        { label: 'Dentaire (soins, couronnes)', value: '—' },
        { label: 'Hospitalisation', value: 'Couverture de base' },
        { label: 'Médecines douces', value: '—' },
      ],
    },
    en: {
      summary: 'Health cover including basic health reimbursements + your specialist appointments.',
      rows: [
        { label: 'Doctor & pharmacy', value: 'Basic cover (excluding psychiatry and paramedical fees: physiotherapists, chiropodists, podiatrists, nurses...)' },
        { label: 'Optical (glasses, lenses)', value: 'Basic cover' },
        { label: 'Dental (care, crowns)', value: '—' },
        { label: 'Hospitalisation', value: 'Basic cover' },
        { label: 'Alternative medicine', value: '—' },
      ],
    },
  },
  confort: {
    fr: {
      summary: "Le confort d'une couverture santé incluant les soins dentaires et optiques.",
      rows: [
        { label: 'Médecin et pharmacie', value: 'Couverture de base' },
        { label: 'Optique (lunettes)', value: 'De 50 à 200€ tous les deux ans' },
        { label: 'Dentaire (soins, couronnes)', value: 'Couverture de base' },
        { label: 'Hospitalisation', value: 'Couverture de base' },
        { label: 'Forfait fruits et légumes', value: '30€/an' },
        { label: 'Taxi soirée (22h–6h)', value: '15€/an' },
        { label: 'Pilule contraceptive', value: '40€/an' },
        { label: 'Médicaments hors prescription', value: '20€/an' },
        { label: 'Forfait test de grossesse', value: '10€/an' },
        { label: 'Médecine douce', value: '30€/an' },
        { label: 'Prévention : préservatifs', value: '20€/an' },
        { label: 'Prévention : protections périodiques', value: '30€/an' },
        { label: 'Vaccins non remboursés par la Sécurité sociale', value: '60€/an' },
        { label: 'Consultation psychologue', value: '40€/an' },
      ],
    },
    en: {
      summary: 'The comfort of health cover including dental and optical care.',
      rows: [
        { label: 'Doctor & pharmacy', value: 'Basic cover' },
        { label: 'Optical (glasses)', value: 'From €50 to €200 every two years' },
        { label: 'Dental (care, crowns)', value: 'Basic cover' },
        { label: 'Hospitalisation', value: 'Basic cover' },
        { label: 'Fruit & vegetable allowance', value: '€30/year' },
        { label: 'Evening taxi (10pm–6am)', value: '€15/year' },
        { label: 'Contraceptive pill', value: '€40/year' },
        { label: 'Over-the-counter medication', value: '€20/year' },
        { label: 'Pregnancy test allowance', value: '€10/year' },
        { label: 'Alternative medicine', value: '€30/year' },
        { label: 'Prevention: condoms', value: '€20/year' },
        { label: 'Prevention: period products', value: '€30/year' },
        { label: 'Vaccines not reimbursed by Social Security', value: '€60/year' },
        { label: 'Psychologist consultation', value: '€40/year' },
      ],
    },
  },
  allRisk: {
    fr: {
      summary: "Prise en charge de tes dépenses en optique, dentaire, auditif + tes rdv chez tes spécialistes même avec dépassement d'honoraire + des forfaits dédiés aux étudiants !",
      rows: [
        { label: 'Médecin et pharmacie', value: 'Couverture maximale' },
        { label: 'Optique (lunettes)', value: 'De 120 à 180€ tous les deux ans' },
        { label: 'Dentaire (soins, couronnes)', value: 'Couverture maximale' },
        { label: 'Hospitalisation', value: 'Couverture maximale + 15€ chambre particulière pendant 30 jours' },
        { label: 'Forfait fruits et légumes', value: '40€/an' },
        { label: 'Taxi soirée (22h–6h)', value: '15€/an' },
        { label: 'Pilule contraceptive', value: '60€/an' },
        { label: 'Médicaments hors prescription', value: '30€/an' },
        { label: 'Forfait test de grossesse', value: '10€/an' },
        { label: 'Médecine douce', value: '60€/an' },
        { label: 'Prévention : préservatifs', value: '30€/an' },
        { label: 'Prévention : protections périodiques', value: '60€/an' },
        { label: 'Vaccins non remboursés par la Sécurité sociale', value: '90€/an' },
        { label: 'Consultation psychologue', value: '80€/an' },
      ],
    },
    en: {
      summary: 'Coverage of your optical, dental and hearing costs + your specialist appointments even with excess fees + dedicated student allowances!',
      rows: [
        { label: 'Doctor & pharmacy', value: 'Maximum cover' },
        { label: 'Optical (glasses)', value: 'From €120 to €180 every two years' },
        { label: 'Dental (care, crowns)', value: 'Maximum cover' },
        { label: 'Hospitalisation', value: 'Maximum cover + €15 private room for 30 days' },
        { label: 'Fruit & vegetable allowance', value: '€40/year' },
        { label: 'Evening taxi (10pm–6am)', value: '€15/year' },
        { label: 'Contraceptive pill', value: '€60/year' },
        { label: 'Over-the-counter medication', value: '€30/year' },
        { label: 'Pregnancy test allowance', value: '€10/year' },
        { label: 'Alternative medicine', value: '€60/year' },
        { label: 'Prevention: condoms', value: '€30/year' },
        { label: 'Prevention: period products', value: '€60/year' },
        { label: 'Vaccines not reimbursed by Social Security', value: '€90/year' },
        { label: 'Psychologist consultation', value: '€80/year' },
      ],
    },
  },
}

/** Offres RC (assurance vie étudiante) : security / indispensable. */
export const civilDetails: Record<string, Record<string, PlanDetail>> = {
  security: {
    fr: {
      summary: "L'assurance vie étudiante complète avec une couverture des risques numériques incluse.",
      rows: [
        { label: 'Responsabilité civile (biens personnels / dommages matériels)', value: 'Incluse' },
        { label: 'Accidents corporels', value: 'Inclus' },
        { label: 'Défense et recours (assistance juridique)', value: 'Inclus' },
        { label: 'Assistance', value: 'Incluse' },
        { label: "Internet / e-réputation / usurpation d'identité", value: 'Inclus' },
        { label: 'Dommages aux biens du maître de stage', value: "Jusqu'à 6 mois" },
      ],
    },
    en: {
      summary: 'The complete student life insurance, with digital-risk cover included.',
      rows: [
        { label: 'Personal liability (personal property / material damage)', value: 'Included' },
        { label: 'Personal accidents', value: 'Included' },
        { label: 'Legal defence and recourse (legal assistance)', value: 'Included' },
        { label: 'Assistance', value: 'Included' },
        { label: 'Internet / e-reputation / identity theft', value: 'Included' },
        { label: "Damage to internship host's property", value: 'Up to 6 months' },
      ],
    },
  },
  indispensable: {
    fr: {
      summary: 'La responsabilité civile indispensable à ta vie étudiante.',
      rows: [
        { label: 'Responsabilité civile (biens personnels / dommages matériels)', value: 'Incluse' },
        { label: 'Accidents corporels', value: 'Inclus' },
        { label: 'Défense et recours (assistance juridique)', value: 'Inclus' },
        { label: 'Assistance', value: 'Incluse' },
        { label: "Internet / e-réputation / usurpation d'identité", value: 'Non inclus' },
        { label: 'Dommages aux biens du maître de stage', value: "Jusqu'à 6 mois" },
      ],
    },
    en: {
      summary: 'The personal liability cover essential to your student life.',
      rows: [
        { label: 'Personal liability (personal property / material damage)', value: 'Included' },
        { label: 'Personal accidents', value: 'Included' },
        { label: 'Legal defence and recourse (legal assistance)', value: 'Included' },
        { label: 'Assistance', value: 'Included' },
        { label: 'Internet / e-reputation / identity theft', value: 'Not included' },
        { label: "Damage to internship host's property", value: 'Up to 6 months' },
      ],
    },
  },
}

/** Solidarité exam (AVE) : allocation d'étude en cas d'examen manqué. */
export const examDetails: Record<string, Record<string, PlanDetail>> = {
  solidariteExam: {
    fr: {
      summary: "Une allocation d'étude si une hospitalisation ou le décès d'un parent t'empêche de présenter tes examens et entraîne un redoublement.",
      rows: [
        { label: "Allocation d'étude en cas de non-présentation à un examen afin de couvrir les dépenses de la nouvelle année d'études", value: '5 000€' },
        { label: "L'aide est versée en cas d'hospitalisation personnelle ou de décès de l'un des parents et si cela entraîne le redoublement et que l'étudiant repasse ses examens", value: '' },
      ],
    },
    en: {
      summary: 'A study allowance if a hospitalisation or the death of a parent prevents you from sitting your exams and forces you to repeat the year.',
      rows: [
        { label: 'Study allowance if you cannot sit an exam, to cover the costs of the new academic year', value: '€5,000' },
        { label: 'The aid is paid in the event of your own hospitalisation or the death of a parent, if it causes you to repeat the year and resit your exams', value: '' },
      ],
    },
  },
}

/** Garanties communes des packs « étranger » (hors zone géographique). */
const abroadGuarantees = (locale: 'fr' | 'en') => {
  const fr = [
    { label: 'Durée de couverture', value: "Jusqu'à 24 mois" },
    { label: 'Frais médicaux', value: 'Jusqu’à 350 000€' },
    { label: 'Dentaire', value: 'Limités à 230€' },
    { label: 'Hospitalisation', value: 'Frais réels remboursés à 100% sans franchise et avec avance de frais' },
    { label: 'Rapatriement sanitaire', value: 'Inclus 24h/24, 7j/7' },
    { label: 'Assistance juridique', value: 'Incluse' },
    { label: 'Responsabilité civile privée', value: 'Incluse' },
    { label: 'Assurance locative', value: 'Incluse' },
    { label: 'Assurance bagages', value: 'Incluse' },
    { label: 'Retour anticipé', value: 'Billet retour simple' },
    { label: 'Envoi de médicaments', value: 'Inclus' },
  ]
  const en = [
    { label: 'Cover duration', value: 'Up to 24 months' },
    { label: 'Medical expenses', value: 'Up to €350,000' },
    { label: 'Dental', value: 'Capped at €230' },
    { label: 'Hospitalisation', value: 'Actual costs reimbursed 100% with no excess and upfront payment' },
    { label: 'Medical repatriation', value: 'Included 24/7' },
    { label: 'Legal assistance', value: 'Included' },
    { label: 'Private personal liability', value: 'Included' },
    { label: 'Tenant insurance', value: 'Included' },
    { label: 'Baggage insurance', value: 'Included' },
    { label: 'Early return', value: 'One-way return ticket' },
    { label: 'Medication delivery', value: 'Included' },
  ]
  return locale === 'en' ? en : fr
}

/** Packs « étudier à l'étranger » : packEurope / packMonde. */
export const abroadDetails: Record<string, Record<string, PlanDetail>> = {
  packEurope: {
    fr: {
      summary: "L'assurance internationale qui te couvre pendant tes études, ton Erasmus ou ton stage en Europe.",
      rows: [{ label: 'Zone géographique', value: 'Europe' }, ...abroadGuarantees('fr')],
    },
    en: {
      summary: 'The international cover that protects you during your studies, Erasmus or internship in Europe.',
      rows: [{ label: 'Geographical area', value: 'Europe' }, ...abroadGuarantees('en')],
    },
  },
  packMonde: {
    fr: {
      summary: "L'assurance internationale qui te couvre pendant tes études ou ton stage dans le monde entier.",
      rows: [{ label: 'Zone géographique', value: 'Monde entier' }, ...abroadGuarantees('fr')],
    },
    en: {
      summary: 'The international cover that protects you during your studies or internship anywhere in the world.',
      rows: [{ label: 'Geographical area', value: 'Worldwide' }, ...abroadGuarantees('en')],
    },
  },
}

/** Packs « j'arrive en France » : packFranceConfort / packFranceGlobal. */
export const franceDetails: Record<string, Record<string, PlanDetail>> = {
  packFranceConfort: {
    fr: {
      summary: 'Une offre pensée pour les étudiants internationaux déjà inscrits à la Sécurité Sociale en France, pour un séjour de + de 3 mois.',
      rows: [
        { label: 'Sécurité sociale', value: 'Non incluse' },
        { label: 'Hospitalisation', value: 'Couverture de base' },
        { label: 'Optique / Dentaire', value: 'Couverture de base' },
        { label: 'Forfait fruits et légumes', value: '30€/an' },
        { label: 'Taxi soirée (22h-6h)', value: '15€/an' },
        { label: 'Pilule contraceptive', value: '40€/an' },
        { label: 'Médicaments hors prescription', value: '20€/an' },
        { label: 'Forfait test de grossesse', value: '10€/an' },
        { label: 'Médecine douce', value: '30€/an' },
        { label: 'Prévention : préservatifs', value: '20€/an' },
        { label: 'Prévention : protections périodiques', value: '30€/an' },
        { label: 'Vaccins non remboursés par la Sécurité sociale', value: '60€/an' },
        { label: 'Consultation psychologue', value: '40€/an' },
        { label: 'Responsabilité civile', value: 'Incluse' },
        { label: 'Assistance / rapatriement', value: 'Inclus' },
      ],
    },
    en: {
      summary: 'A plan for international students already registered with the French Social Security, for a stay of over 3 months.',
      rows: [
        { label: 'Social Security', value: 'Not included' },
        { label: 'Hospitalisation', value: 'Basic cover' },
        { label: 'Optical / Dental', value: 'Basic cover' },
        { label: 'Fruit & vegetable allowance', value: '€30/year' },
        { label: 'Evening taxi (10pm-6am)', value: '€15/year' },
        { label: 'Contraceptive pill', value: '€40/year' },
        { label: 'Over-the-counter medication', value: '€20/year' },
        { label: 'Pregnancy test allowance', value: '€10/year' },
        { label: 'Alternative medicine', value: '€30/year' },
        { label: 'Prevention: condoms', value: '€20/year' },
        { label: 'Prevention: period products', value: '€30/year' },
        { label: 'Vaccines not reimbursed by Social Security', value: '€60/year' },
        { label: 'Psychologist consultation', value: '€40/year' },
        { label: 'Personal liability', value: 'Included' },
        { label: 'Assistance / repatriation', value: 'Included' },
      ],
    },
  },
  packFranceGlobal: {
    fr: {
      summary: 'Une offre pensée pour les étudiants internationaux non-inscrits à la Sécurité Sociale en France, pour un séjour de moins de 3 mois.',
      rows: [
        { label: 'Sécurité sociale', value: 'Incluse' },
        { label: 'Hospitalisation', value: 'Prise en charge jusqu’à 100 % selon niveau de dépenses' },
        { label: 'Soins dentaires', value: '150€/trimestre pour les soins urgents' },
        { label: 'Optique', value: 'Pas de prise en charge' },
        { label: 'Pharmacie, radio et analyses', value: '750€/trimestre max.' },
        { label: 'Responsabilité civile', value: 'Incluse' },
        { label: 'Assistance / rapatriement', value: 'Inclus' },
      ],
    },
    en: {
      summary: 'A plan for international students not registered with the French Social Security, for a stay of under 3 months.',
      rows: [
        { label: 'Social Security', value: 'Included' },
        { label: 'Hospitalisation', value: 'Covered up to 100% depending on expense level' },
        { label: 'Dental care', value: '€150/quarter for emergency care' },
        { label: 'Optical', value: 'Not covered' },
        { label: 'Pharmacy, X-ray and tests', value: '€750/quarter max.' },
        { label: 'Personal liability', value: 'Included' },
        { label: 'Assistance / repatriation', value: 'Included' },
      ],
    },
  },
}

/** Formules mutuelle Auvergne-Rhône-Alpes : primordiale / renforcee. */
export const regionalDetails: Record<string, Record<string, PlanDetail>> = {
  primordiale: {
    fr: {
      summary: 'Une couverture santé incluant les soins dentaires et optiques.',
      rows: [
        { label: 'Médecin et pharmacie', value: 'Couverture de base' },
        { label: 'Optique (lunettes)', value: 'De 50 à 200€ tous les deux ans' },
        { label: 'Dentaire (soins, couronnes)', value: 'Couverture de base' },
        { label: 'Hospitalisation', value: 'Couverture de base' },
        { label: 'Pilule contraceptive', value: '40€/an' },
        { label: 'Médicaments hors prescription', value: '20€/an' },
        { label: 'Forfait test de grossesse', value: '10€/an' },
        { label: 'Médecine douce', value: '30€/an' },
        { label: 'Prévention : protections périodiques', value: '30€/an' },
        { label: 'Vaccins non remboursés par la Sécurité sociale', value: '60€/an' },
        { label: 'Consultation psychologue', value: '40€/an' },
        { label: 'Abonnement clubs sportifs', value: '30€/an' },
      ],
    },
    en: {
      summary: 'Health cover including dental and optical care.',
      rows: [
        { label: 'Doctor & pharmacy', value: 'Basic cover' },
        { label: 'Optical (glasses)', value: '€50 to €200 every two years' },
        { label: 'Dental (care, crowns)', value: 'Basic cover' },
        { label: 'Hospitalisation', value: 'Basic cover' },
        { label: 'Contraceptive pill', value: '€40/year' },
        { label: 'Over-the-counter medication', value: '€20/year' },
        { label: 'Pregnancy test allowance', value: '€10/year' },
        { label: 'Alternative medicine', value: '€30/year' },
        { label: 'Prevention: period products', value: '€30/year' },
        { label: 'Vaccines not reimbursed by Social Security', value: '€60/year' },
        { label: 'Psychologist consultation', value: '€40/year' },
        { label: 'Sports club membership', value: '€30/year' },
      ],
    },
  },
  renforcee: {
    fr: {
      summary: "Prise en charge de tes dépenses en optique, dentaire, auditif + tes rdv chez tes spécialistes même avec dépassement d'honoraire + des forfaits dédiés aux étudiants.",
      rows: [
        { label: 'Médecin et pharmacie', value: 'Couverture maximale' },
        { label: 'Optique (lunettes)', value: 'De 120 à 280€ tous les deux ans' },
        { label: 'Dentaire (soins, couronnes)', value: 'Couverture maximale' },
        { label: 'Hospitalisation', value: 'Couverture maximale + 15€ chambre particulière pendant 30 jours' },
        { label: 'Pilule contraceptive', value: '40€/an' },
        { label: 'Médicaments hors prescription', value: '30€/an' },
        { label: 'Forfait test de grossesse', value: '10€/an' },
        { label: 'Médecine douce', value: '60€/an' },
        { label: 'Prévention : protections périodiques', value: '50€/an' },
        { label: 'Vaccins non remboursés par la Sécurité sociale', value: '60€/an' },
        { label: 'Consultation psychologue', value: '60€/an' },
        { label: 'Abonnement clubs sportifs', value: '50€/an' },
      ],
    },
    en: {
      summary: 'Coverage of your optical, dental and hearing costs + specialist appointments even with excess fees + dedicated student allowances.',
      rows: [
        { label: 'Doctor & pharmacy', value: 'Maximum cover' },
        { label: 'Optical (glasses)', value: '€120 to €280 every two years' },
        { label: 'Dental (care, crowns)', value: 'Maximum cover' },
        { label: 'Hospitalisation', value: 'Maximum cover + €15 private room for 30 days' },
        { label: 'Contraceptive pill', value: '€40/year' },
        { label: 'Over-the-counter medication', value: '€30/year' },
        { label: 'Pregnancy test allowance', value: '€10/year' },
        { label: 'Alternative medicine', value: '€60/year' },
        { label: 'Prevention: period products', value: '€50/year' },
        { label: 'Vaccines not reimbursed by Social Security', value: '€60/year' },
        { label: 'Psychologist consultation', value: '€60/year' },
        { label: 'Sports club membership', value: '€50/year' },
      ],
    },
  },
}

/** Surcomplémentaire Tonic-Études (clé `tonic`), indexée par locale. */
export const topUpDetailContent: Record<Locale, PlanDetail> = {
  fr: {
    summary: 'La surcomplémentaire étudiante qui rembourse ta vie étudiante (taxi, fruits et légumes, ostéo…), et renforce ta complémentaire santé actuelle.',
    rows: [
      { label: 'Forfait fruits et légumes', value: '40€/an' },
      { label: 'Taxi soirée (entre 22h et 6h)', value: '15€/an' },
      { label: 'Pilule contraceptive', value: '60€/an' },
      { label: 'Médicaments hors prescription', value: '20€/an' },
      { label: 'Forfait test de grossesse', value: '10€/an' },
      { label: 'Médecine douce', value: '50€/an' },
      { label: 'Prévention : préservatifs', value: '20€/an' },
      { label: 'Protections périodiques', value: '30€/an' },
      { label: 'Optique : lentilles non remboursables', value: '50€/an' },
      { label: 'Vaccins non remboursés par la Sécurité sociale', value: '60€/an' },
    ],
  },
  en: {
    summary: 'The student top-up cover that reimburses your student life (taxi, fruit & vegetables, osteopathy…) and boosts your current health cover.',
    rows: [
      { label: 'Fruit & vegetable allowance', value: '€40/year' },
      { label: 'Evening taxi (10pm–6am)', value: '€15/year' },
      { label: 'Contraceptive pill', value: '€60/year' },
      { label: 'Over-the-counter medication', value: '€20/year' },
      { label: 'Pregnancy test allowance', value: '€10/year' },
      { label: 'Alternative medicine', value: '€50/year' },
      { label: 'Prevention: condoms', value: '€20/year' },
      { label: 'Period products', value: '€30/year' },
      { label: 'Optical: non-reimbursable lenses', value: '€50/year' },
      { label: 'Vaccines not reimbursed by Social Security', value: '€60/year' },
    ],
  },
}

/** Lookup unifié par slug de formule (toutes familles confondues). */
const BY_SLUG: Partial<Record<string, Record<string, PlanDetail>>> = {
  ...planDetails,
  ...civilDetails,
  ...examDetails,
  ...abroadDetails,
  ...franceDetails,
  ...regionalDetails,
  tonic: topUpDetailContent,
}

/**
 * A guarantee table plus the link to its Calaméo sheet — what every
 * "En savoir +" panel needs. `sheetUrl` is null when the formula has no
 * published sheet, in which case consumers omit the link entirely.
 */
export type GuaranteeDetail = PlanDetail & { sheetUrl: string | null }

/** Détail de garanties pour un slug de formule, dans la locale demandée
 *  (repli sur le français). Renvoie `undefined` si la formule n'a pas de table.
 *
 *  The Calaméo sheet is resolved here rather than by each caller so the sales
 *  journey and the connected space cannot drift apart on which document a
 *  formula points to — the reported SI-1455 defect. A formula with rows but no
 *  sheet still returns a detail, with `sheetUrl` at null. */
export function guaranteeDetailFor(slug: string, locale: string): GuaranteeDetail | undefined {
  const byLocale = BY_SLUG[slug]
  const detail = byLocale?.[locale] ?? byLocale?.fr
  if (!detail) return undefined
  return { ...detail, sheetUrl: guaranteeSheetUrlFor(slug) }
}
